import { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
import { MediaTypeProvider } from './contexts/MediaTypeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';
import { UserLibraryProvider } from './contexts/UserLibraryContext';
import { ListsProvider } from './contexts/ListsContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AppShell } from './components/layout';
import { ChatBox } from './components/ChatBox';
import { FilmFinder } from './components/FilmFinder';
import { MovieDetail } from './components/MovieDetail';
import { TVDetail } from './components/TVDetail';
import { PersonDetail } from './components/PersonDetail';
import { AuthCallback } from './pages/AuthCallback';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { ProfilePage } from './pages/ProfilePage';
import { FollowersPage } from './pages/FollowersPage';
import { WatchlistPage } from './pages/WatchlistPage';
import { DeletedListsPage } from './pages/DeletedListsPage';
import { IFeelPage } from './pages/IFeelPage';
import { ForYouPage } from './pages/ForYouPage';
import { PricingPage } from './pages/PricingPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { CookiePolicyPage } from './pages/CookiePolicyPage';
import { TermsPage } from './pages/TermsPage';
import { DisclaimerPage } from './pages/DisclaimerPage';
import { RecommendationsInboxPage } from './pages/RecommendationsInboxPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { InboxLayoutPage } from './pages/InboxLayoutPage';
import { InboxThreadPage } from './pages/InboxThreadPage';
import { SettingsPage } from './pages/SettingsPage';
import { FilmplyPage } from './pages/FilmplyPage';
import {
  AdminHomePage,
  AdminAiTaggingPage,
  AdminCompletenessPage,
  AdminConfigPage,
  AdminCohortsPage,
  AdminJobsPage,
  AdminJobRunDetailPage,
  AdminReviewDetailPage,
  AdminSyncPage,
  AdminSyncRunDetailPage,
} from './pages/admin';
import { UsernameModal } from './components/UsernameModal';
import { ScrollToTop } from './components/ScrollToTop';
import { ToastProvider } from './components/ui/Toast';
import { QueryCachePersister } from './components/QueryCachePersister';
import { useOutboxReplay } from './hooks/useOutboxReplay';
import { BootRevalidator } from './components/BootRevalidator';
import { SessionExpiredHandler } from './components/SessionExpiredHandler';
import { AccountClosedHandler } from './components/AccountClosedHandler';
import { AccountDeactivatedGate } from './components/settings/AccountDeactivatedGate';
import { AccountSuspendedGate } from './components/settings/AccountSuspendedGate';
import { IOSWebGate } from './components/IOSWebGate';
import {
  isIntercomConfigured,
  ensureIntercomBoot,
  updateIntercom,
  shutdownIntercom,
} from './lib/intercom';
import { apiFetch } from './lib/api';

const APP_STORE_URL = 'https://apps.apple.com/app/filmple/id6759590295';

function IFeelComingSoonPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#111111] px-4 sm:px-6 py-8">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
          aria-label="Go back"
        >
          <span className="text-sm font-medium">Back</span>
        </button>

        <div className="bg-[#1A1F26]/50 ring-1 ring-white/[0.08] rounded-2xl p-8 text-center">
          <h1 className="text-2xl font-bold text-white mb-2">I Feel</h1>
          <p className="text-slate-400">Coming soon.</p>
        </div>
      </div>
    </div>
  );
}

function DownloadRedirectPage() {
  useEffect(() => {
    window.location.replace(APP_STORE_URL);
  }, []);

  return (
    <div className="min-h-screen bg-[#111111] flex items-center justify-center">
      <p className="text-sm text-slate-400">Redirecting to the App Store...</p>
    </div>
  );
}

function IFeelRouteGate() {
  const { profile } = useAuth();

  if (profile?.is_test_user) {
    return <IFeelPage />;
  }

  return <IFeelComingSoonPage />;
}

/**
 * OutboxReplayBootstrap - Mounts exactly once inside AuthProvider + QueryClientProvider.
 * Handles outbox replay on boot, reconnect, focus, and clears on logout/user switch.
 */
function OutboxReplayBootstrap() {
  useOutboxReplay();
  return null;
}

/**
 * PostHogPageview - Captures $pageview events on SPA route changes.
 * Must be inside BrowserRouter to access useLocation().
 */
function PostHogPageview() {
  const location = useLocation();
  const lastPathRef = useRef<string | null>(null);

  useEffect(() => {
    // Skip if PostHog not initialized
    if (typeof posthog.capture !== 'function') return;

    // Skip duplicate captures (React StrictMode can double-fire effects)
    const currentPath = location.pathname + location.search;
    if (lastPathRef.current === currentPath) return;
    lastPathRef.current = currentPath;

    posthog.capture('$pageview', {
      $current_url: window.location.href,
    });
  }, [location]);

  return null;
}

/**
 * IntercomRouteUpdate - Calls Intercom('update') on route changes.
 * Throttled: path dedupe + minimum 90s interval to stay within 20/30min limit.
 * Must be inside BrowserRouter to access useLocation().
 */
function IntercomRouteUpdate() {
  const location = useLocation();
  const lastPathRef = useRef<string | null>(null);
  const lastUpdateRef = useRef<number>(0);

  useEffect(() => {
    if (!isIntercomConfigured) return;

    const currentPath = location.pathname;

    // Path dedupe — skip if same page
    if (lastPathRef.current === currentPath) return;
    lastPathRef.current = currentPath;

    // Time throttle — minimum 90s between updates
    const now = Date.now();
    if (now - lastUpdateRef.current < 90_000) return;
    lastUpdateRef.current = now;

    updateIntercom();
  }, [location]);

  return null;
}

/**
 * PostHogIdentify - Identifies/resets PostHog user based on auth state.
 * Must be inside AuthProvider to access useAuth().
 */
function PostHogIdentify() {
  const { user, profile } = useAuth();
  const identifiedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Skip if PostHog not initialized
    if (typeof posthog.identify !== 'function') return;

    if (user && profile) {
      // Skip if already identified this user (prevent duplicate identify calls)
      if (identifiedUserIdRef.current === user.id) return;
      identifiedUserIdRef.current = user.id;

      posthog.identify(user.id, {
        email: user.email,
        username: profile.username,
        is_pro: profile.is_pro,
        is_test_user: profile.is_test_user,
        visibility: profile.visibility,
        created_at: profile.created_at,
      });
    } else if (identifiedUserIdRef.current !== null) {
      // User logged out - reset PostHog
      identifiedUserIdRef.current = null;
      posthog.reset();
    }
  }, [user, profile]);

  return null;
}

/**
 * IntercomBoot - Boots/shuts down Intercom based on auth state.
 * Follows PostHogIdentify pattern: null-render, StrictMode-safe dedupe via ref.
 *
 * Ref states: null (idle) | 'pending' (fetching JWT) | userId (booted).
 * Cancellation guard prevents stale async from booting after logout/user-switch.
 */
function IntercomBoot() {
  const { user, session } = useAuth();
  const bootedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isIntercomConfigured) return;

    if (user && session?.access_token) {
      // Skip if already booted for this user
      if (bootedUserIdRef.current === user.id) return;
      // If a previous boot is in-flight ('pending'), the cleanup below will cancel it
      bootedUserIdRef.current = 'pending';

      let cancelled = false;

      (async () => {
        try {
          const data = await apiFetch<{ token: string; expires_in: number }>(
            '/user/intercom-jwt',
            { token: session.access_token }
          );

          // Guard: user may have logged out or switched during the fetch
          if (cancelled) return;

          const didBoot = await ensureIntercomBoot(data.token, user.id, user.email);
          if (!cancelled) {
            bootedUserIdRef.current = didBoot ? user.id : null;
          }
        } catch {
          // Intercom boot is non-critical — fail silently, reset ref to allow retry
          if (!cancelled) {
            bootedUserIdRef.current = null;
          }
        }
      })();

      return () => {
        cancelled = true;
        // Reset pending state so the next effect run (new user / re-login) is not blocked
        if (bootedUserIdRef.current === 'pending') {
          bootedUserIdRef.current = null;
        }
      };
    } else if (bootedUserIdRef.current !== null) {
      // User logged out — shutdown Intercom to prevent identity leakage
      bootedUserIdRef.current = null;
      shutdownIntercom();
    }
  }, [user, session]);

  return null;
}

function AppContent() {
  const { user, profile, profileStatus, needsUsername, setProfileUsername, refreshProfile, signOut } = useAuth();

  // Profile loading fallback (safety net - should rarely trigger after atomic login)
  // If profile exists in cache/memory, skip this gate - cached UI renders instantly
  if (user && profileStatus === 'loading' && !profile) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full bg-[#1A1F26] animate-pulse" />
      </div>
    );
  }

  // Profile fetch error: show recoverable error UI (retry / sign out)
  if (user && profileStatus === 'error') {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="text-center p-8 max-w-sm">
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-white mb-2">Something went wrong</h2>
          <p className="text-sm text-slate-400 mb-6">
            {"We couldn't load your profile. Please try again."}
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => refreshProfile()}
              className="w-full py-2.5 bg-cyan-500 hover:bg-cyan-400 text-white font-medium rounded-lg transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => signOut()}
              className="w-full py-2.5 bg-[#1A1F26] hover:bg-[#252B33] text-slate-300 font-medium rounded-lg transition-colors ring-1 ring-white/[0.06]"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Missing profile: user is authenticated but has no profile row yet (new user)
  // Show skeleton shell with username modal overlay - don't mount full app with null profile
  if (user && profileStatus === 'missing') {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full bg-[#1A1F26] animate-pulse" />
        {needsUsername && (
          <UsernameModal isOpen={true} onComplete={setProfileUsername} />
        )}
      </div>
    );
  }

  // Hard gate: Deactivated accounts cannot browse the app (Instagram-style)
  // They can only restore or sign out - no dismissing allowed
  if (user && profile?.deleted_at) {
    return <AccountDeactivatedGate />;
  }

  // Hard gate: Suspended accounts — no restore, just sign out
  if (user && profile?.is_suspended) {
    return <AccountSuspendedGate />;
  }

  return (
    <>
      <AppShell>
        <Routes>
          <Route path="/" element={<FilmFinder />} />
          <Route path="/movie/:id" element={<MovieDetail />} />
          <Route path="/tv/:id" element={<TVDetail />} />
          <Route path="/person/:id" element={<PersonDetail />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/u/:username" element={<ProfilePage />} />
          <Route path="/u/:username/followers" element={<FollowersPage />} />
          <Route path="/u/:username/following" element={<FollowersPage />} />
          <Route path="/lists/deleted" element={<DeletedListsPage />} />
          <Route path="/list/:id" element={<WatchlistPage />} />
          <Route path="/feel" element={<IFeelRouteGate />} />
          <Route path="/for-you" element={<ForYouPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/download" element={<DownloadRedirectPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/filmply" element={<FilmplyPage />} />
          <Route path="/recommendations" element={<RecommendationsInboxPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          {/* Inbox v2: WhatsApp-style split view with nested routes */}
          <Route path="/inbox" element={<InboxLayoutPage />}>
            <Route path=":username" element={<InboxThreadPage />} />
          </Route>

          {/* Admin Console (150% safe gating) */}
          <Route path="/admin" element={<AdminHomePage />} />
          <Route path="/admin/ai-tagging" element={<AdminAiTaggingPage />} />
          <Route path="/admin/completeness" element={<AdminCompletenessPage />} />
          <Route path="/admin/config" element={<AdminConfigPage />} />
          <Route path="/admin/cohorts" element={<AdminCohortsPage />} />
          <Route path="/admin/jobs" element={<AdminJobsPage />} />
          <Route path="/admin/jobs/:id" element={<AdminJobRunDetailPage />} />
          <Route path="/admin/reviews/:id" element={<AdminReviewDetailPage />} />
          <Route path="/admin/sync" element={<AdminSyncPage />} />
          <Route path="/admin/sync/:id" element={<AdminSyncRunDetailPage />} />
        </Routes>
      </AppShell>
      <ChatBox />

      {/* Username selection modal - shown globally when user needs to set username */}
      {user && needsUsername && (
        <UsernameModal
          isOpen={true}
          onComplete={setProfileUsername}
        />
      )}
    </>
  );
}

function App() {
  return (
    <ToastProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          {/* Phase 5F: Hydration gate - blocks children until cache is ready */}
          <QueryCachePersister>
            {/* Outbox replay: mounts once, handles boot replay + logout cleanup */}
            <OutboxReplayBootstrap />
            {/* PostHog: identify user on login, reset on logout */}
            <PostHogIdentify />
            {/* Intercom: boot on login, shutdown on logout */}
            <IntercomBoot />
            {/* Session expired handler: listens for 401s and signs out + shows toast */}
            <SessionExpiredHandler />
            {/* Account closed handler: listens for 403 ACCOUNT_SUSPENDED and signs out + shows toast */}
            <AccountClosedHandler />
            <NotificationProvider>
              <ListsProvider>
                <UserLibraryProvider>
                  <ChatProvider>
                    <MediaTypeProvider>
                      <BrowserRouter>
                        {/* PostHog: capture $pageview on route changes */}
                        <PostHogPageview />
                        {/* Intercom: update on route changes (throttled) */}
                        <IntercomRouteUpdate />
                        {/* Boot revalidation: must be inside BrowserRouter for useLocation */}
                        <BootRevalidator />
                        <ScrollToTop />
                        <IOSWebGate>
                          <AppContent />
                        </IOSWebGate>
                      </BrowserRouter>
                    </MediaTypeProvider>
                  </ChatProvider>
                </UserLibraryProvider>
              </ListsProvider>
            </NotificationProvider>
          </QueryCachePersister>
        </AuthProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ToastProvider>
  );
}

export default App;
